import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Alert,
  Paper,
  CircularProgress,
} from '@mui/material';
import { renderComponentById } from '@/shared/factories/componentFactory';
import { renderWidgetWithErrorBoundary } from '@/shared/factories/widgetFactory';
import { SnackbarComponent } from '@/shared/ui';
import { LayoutConfig, PageConfig } from '@/entities/user';

interface LayoutBuilderProps {
  pageConfig: PageConfig;
  onWidgetAction?: (widgetId: string, action: string, data?: any) => void;
  onError?: (error: Error, componentId?: string) => void;
}

export const LayoutBuilder: React.FC<LayoutBuilderProps> = ({
  pageConfig,
  onWidgetAction,
  onError,
}) => {
  // Error handling
  const handleError = React.useCallback(
    (error: Error, componentId?: string) => {
      console.error(
        `Layout Builder Error in ${componentId || 'unknown component'}:`,
        error
      );
      if (onError) {
        onError(error, componentId);
      }
    },
    [onError]
  );

  // Widget action handler
  const createWidgetActionHandler = React.useCallback(
    (widgetId: string) => {
      return (action: string, data?: any) => {
        if (onWidgetAction) {
          onWidgetAction(widgetId, action, data);
        }
      };
    },
    [onWidgetAction]
  );

  return (
    <Box
      sx={{
        minHeight: '100%',
      }}
    >
      {/* Render Components (Header, Sidebar, etc.) */}
      {pageConfig.components?.map(componentConfig => {
        try {
          const component = renderComponentById(componentConfig.id, {
            ...componentConfig.props,
            key: componentConfig.id,
          });

          return <Box key={componentConfig.id}>{component}</Box>;
        } catch (error) {
          handleError(error as Error, componentConfig.id);
          return (
            <Alert key={componentConfig.id} severity="error">
              Failed to render component "{componentConfig.id}"
            </Alert>
          );
        }
      })}

      {/* Render Widgets */}
      {pageConfig.widgets &&
        Array.isArray(pageConfig.widgets) &&
        pageConfig.widgets.length > 0 && (
          <Box>
            <React.Suspense
              fallback={
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                    flex: 1,
                    minHeight: '60vh',
                  }}
                >
                  <CircularProgress />
                </Box>
              }
            >
              {(Array.isArray(pageConfig.widgets)
                ? [...pageConfig.widgets]
                : []
              )
                .sort((a, b) => a.order - b.order)
                .map(widgetGroup => (
                  <Box key={`group-${widgetGroup.order}`} mb={3}>
                    <Grid container spacing={2}>
                      {widgetGroup.widgets.map(widgetItem => {
                        try {
                          const enhancedProps = {
                            ...widgetItem.props,
                            onAction: createWidgetActionHandler(widgetItem.id),
                            id: widgetItem.id,
                          };

                          const widget = renderWidgetWithErrorBoundary(
                            widgetItem.id,
                            enhancedProps
                          );

                          const gridProps = widgetItem.props?.flex
                            ? { xs: 12, md: 12, lg: 12 }
                            : { xs: 12, md: 6, lg: 4 };

                          return (
                            <Grid item key={widgetItem.id} {...gridProps}>
                              <Paper
                                elevation={0}
                                sx={{
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                }}
                              >
                                {widget}
                              </Paper>
                            </Grid>
                          );
                        } catch (error) {
                          handleError(error as Error, widgetItem.id);
                          return (
                            <Grid
                              item
                              key={widgetItem.id}
                              xs={12}
                              md={6}
                              lg={4}
                            >
                              <Alert severity="error">
                                Failed to render widget "{widgetItem.id}"
                              </Alert>
                            </Grid>
                          );
                        }
                      })}
                    </Grid>
                  </Box>
                ))}
            </React.Suspense>
          </Box>
        )}

      {/* Empty state */}
      {(!pageConfig.widgets || pageConfig.widgets.length === 0) && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No widgets configured
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This page doesn't have any widgets configured yet.
          </Typography>
        </Paper>
      )}

      {/* Debug info in development */}
      {process.env.NODE_ENV === 'development' && (
        <Box mt={4}>
          <details>
            <summary>Layout Debug Info</summary>
            <pre style={{ fontSize: '12px', overflow: 'auto' }}>
              {JSON.stringify(pageConfig, null, 2)}
            </pre>
          </details>
        </Box>
      )}

      {/* Global Snackbar */}
      <SnackbarComponent />
    </Box>
  );
};

// Hook for using layout builder functionality
export const useLayoutBuilder = (pageKey: string, config?: LayoutConfig) => {
  const pageConfig = config?.[pageKey] as PageConfig | undefined;

  const getAvailablePages = React.useCallback(() => {
    if (!config) return [];

    return Object.keys(config).filter(
      key => !['id', 'partner_id', 'routes', 'sidebar'].includes(key)
    );
  }, [config]);

  const getPageMetadata = React.useCallback(
    (key: string) => {
      const page = config?.[key] as PageConfig | undefined;
      return page
        ? {
            title: page.title,
          }
        : null;
    },
    [config]
  );

  const getSidebarConfig = React.useCallback(() => {
    return config?.sidebar || [];
  }, [config]);

  const getRoutes = React.useCallback(() => {
    return config?.routes || [];
  }, [config]);

  return {
    layoutConfig: config,
    pageConfig,
    getAvailablePages,
    getPageMetadata,
    getSidebarConfig,
    getRoutes,
  };
};

export default LayoutBuilder;
